/** The activity record's own inputs (plan §3 "Cards", O14), registered under the edit
 *  catalog's widget names — the records core's built-ins cover the rest (text, enum,
 *  rich_text, duration, opening_hours, number):
 *
 *  - `category` — one of the operator's activity categories (catalog picker `categories`);
 *  - `country` / `currency` — the global lists;
 *  - `coordinates` — ONE key, `{latitude, longitude}`, the map one click away;
 *  - `activity_list` — the nearby activities: other activities of the operator, in order
 *    (catalog picker `activities`), named from the detail;
 *  - `tags` (the catalog's `atags`, `coreWidget`) — the operator's access tags and its
 *    franchise parent's (catalog picker `atags`).
 *
 *  Every option shows its pk, and the operator's own rows its tenant code (the
 *  remote-autocomplete rule). An activity without an operator has nothing to pick from (its
 *  Edit is blocked anyway). Create the set once per activity (`useActivityWidgets`): a new
 *  definition per render would remount the inputs. */
import { useMemo, useRef } from 'react';

import type { ActivityDetail } from '@/api/activities';
import { catalogPicker, type CatalogPickerKind } from '@/api/catalogPickers';
import { listCountries, listCurrencies } from '@/api/pickers';
import type { RecordOptionRef, RecordWidget } from '@/features/records/editor/types';
import { createCoordinatePairWidget } from '@/features/records/fields/widgets/CoordinatesWidget';
import { createOrderedIdListWidget } from '@/features/records/fields/widgets/OrderedIdListWidget';
import { createRecordPicker, createTagsWidget } from '@/features/records/fields/widgets/pickers';

/** A currency as the record words it: "USD ($)", the code alone when the symbol is it. */
export function currencyLabel(c: { code: string; symbol?: string | null }): string {
  return c.symbol && c.symbol !== c.code ? `${c.code} (${c.symbol})` : c.code;
}

const COORDINATES = createCoordinatePairWidget({ place: 'activity' });

async function pickCatalog(
  tenant: ActivityDetail['tenant'],
  kind: CatalogPickerKind,
  q: string,
  signal: AbortSignal,
  group: string,
  ownRows: boolean,
): Promise<RecordOptionRef[]> {
  if (!tenant) return [];
  const { results } = await catalogPicker(tenant.id, kind, { q, signal });
  return results.map((o) => ({
    id: o.id,
    label: o.name ?? `#${o.id}`,
    group,
    // The operator's own rows (categories, activities) name it; access tags may be its
    // franchise parent's, which the picker does not say.
    ...(ownRows ? { tenant: tenant.code } : {}),
  }));
}

/** The activity's widgets, bound to its operator and id. `nearbyName` names a nearby
 *  activity the detail lists (read at call time — a save's re-seeded detail renames it
 *  without remounting the list). */
export function activityWidgets(
  tenant: ActivityDetail['tenant'],
  activityId: number,
  nearbyName: (id: number) => string | null | undefined,
): Record<string, RecordWidget> {
  return {
    category: createRecordPicker({
      load: (_f, _r, q, signal) => pickCatalog(tenant, 'categories', q, signal, 'Categories', true),
      fetchOnEmpty: true,
      placeholder: 'No category',
    }),
    country: createRecordPicker({
      load: async (_f, _r, q) =>
        (await listCountries(q.trim() || undefined)).results.map((c) => ({
          id: c.id,
          label: c.name,
          extra: [c.country_code],
        })),
      placeholder: 'No country',
    }),
    currency: createRecordPicker({
      load: async (_f, _r, q) =>
        (await listCurrencies(q.trim() || undefined)).results.map((c) => ({
          id: c.id,
          label: currencyLabel(c),
          extra: [c.name],
        })),
      fetchOnEmpty: true,
      placeholder: 'No currency',
    }),
    coordinates: COORDINATES,
    activity_list: createOrderedIdListWidget({
      labelOf: nearbyName,
      // Other activities of the operator: never the activity itself.
      load: async (_f, _r, q, signal) =>
        (await pickCatalog(tenant, 'activities', q, signal, 'Activities', true)).filter((o) => o.id !== activityId),
      placeholder: 'Add a nearby activity',
    }),
    tags: createTagsWidget({
      load: (_f, _r, q, signal) => pickCatalog(tenant, 'atags', q, signal, 'Access tags', false),
      placeholder: 'Add an access tag',
      reserveAddRow: true,
    }),
  };
}

/** The widgets of one activity — stable while its operator and id are. */
export function useActivityWidgets(detail: ActivityDetail): Record<string, RecordWidget> {
  const names = useRef(new Map<number, string | null>());
  names.current = new Map(detail.children.map((c) => [c.id, c.name]));
  const { tenant, id } = detail;
  const tenantKey = tenant ? `${tenant.id}:${tenant.code}` : '';
  return useMemo(
    () => activityWidgets(tenant, id, (child) => names.current.get(child)),
    // The pickers depend on the operator and the activity only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantKey, id],
  );
}
